// ── Today: the shop floor at a glance, for managers (Tech Mode part 2, 2 Oct) ──
//
// Three things a manager walking the floor wants to know:
//   - which timers stopped by themselves and need a look (the review panel
//     lives on the Board tab; this links to it)
//   - each tech right now: what they are timing, or what they last timed, and
//     their hours today
//   - Over budget: open tickets whose labor billed works out below the target
//     rate for the time spent on them
//
// The target is per ticket, from the server (helpers/laborRates.js): the blend
// of the people who worked it. One rate per shop today; per tech possibly
// later, with nothing to change here.

import React, { useState, useEffect, useCallback } from 'react';
import { API, authHeaders, roleLabel } from '../utils/helpers';
import { fmtDur, fmtMoney, fmtRate, fmtTime, colors, Panel, IdTag, Row, Empty, DISPLAY_FONT } from './effortUi';

// Less than this on a ticket is too little to judge a rate by: ten minutes
// against a $65 tune-up reads as $390/hr, and two minutes against nothing as $0.
const MIN_SECONDS = 15 * 60;

const clock = (ms) => {
  const m = Math.max(0, Math.floor(ms / 60000));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
};

// Who the viewer sees on "The shop right now" (28 Oct): their own choice,
// saved with their login (PUT /api/effort/today/view). Hidden means hidden.
const ROLE_OPTIONS = ['owner', 'manager', 'tech', 'associate'];
export function visiblePeople(techs, view) {
  const hidden = new Set(((view && view.hidden) || []).map(Number));
  const role = view && view.role;
  return (techs || []).filter(p => !hidden.has(Number(p.userId)) && (!role || p.role === role));
}

function WhoFilter({ T, c, techs, view, onChange }) {
  const [open, setOpen] = useState(false);
  const hidden = new Set(view.hidden.map(Number));
  const toggle = (id) => {
    const next = new Set(hidden);
    if (next.has(Number(id))) next.delete(Number(id)); else next.add(Number(id));
    onChange({ ...view, hidden: [...next] });
  };
  const ctl = { fontSize: 13, padding: '6px 10px', borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.input || 'transparent', color: T.text, fontFamily: 'inherit', cursor: 'pointer' };
  return (
    <span style={{ position: 'relative', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      <select aria-label="Show a role" value={view.role || ''} onChange={e => onChange({ ...view, role: e.target.value || null })} style={ctl}>
        <option value="">Every role</option>
        {ROLE_OPTIONS.map(r => <option key={r} value={r}>{roleLabel(r)}s</option>)}
      </select>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} style={ctl}>
        Choose people{hidden.size ? ` · ${hidden.size} hidden` : ''}
      </button>
      {open && (
        <div role="dialog" aria-label="Choose who is shown"
          style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50, minWidth: 240, maxHeight: 360, overflowY: 'auto',
            background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: 8, boxShadow: '0 12px 32px rgba(0,0,0,0.25)' }}>
          {techs.map(p => (
            <label key={p.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 14, color: T.text, cursor: 'pointer' }}>
              <input type="checkbox" checked={!hidden.has(Number(p.userId))} onChange={() => toggle(p.userId)} />
              <span style={{ flex: 1 }}>{p.name}</span>
              <span style={{ fontSize: 12, color: c.sub }}>{p.role ? roleLabel(p.role) : ''}</span>
            </label>
          ))}
          <div style={{ display: 'flex', gap: 8, borderTop: `1px solid ${T.cardBorder}`, marginTop: 6, paddingTop: 8 }}>
            <button type="button" onClick={() => onChange({ ...view, hidden: [] })} style={{ ...ctl, border: 'none' }}>Show everyone</button>
            <button type="button" onClick={() => setOpen(false)} style={{ ...ctl, marginLeft: 'auto' }}>Done</button>
          </div>
        </div>
      )}
    </span>
  );
}

export default function TodayBoard({ T, isDark, tickets, onOpenTicket, onReview }) {
  const c = colors(isDark);
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);           // the viewer's filter, once loaded
  const [error, setError] = useState('');
  const [skew, setSkew] = useState(0);             // server clock minus this one
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/effort/today`, { credentials: 'include', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load today.');
      setSkew(new Date(d.serverNow) - Date.now());
      setData(d); setError('');
      setView(v => v || d.view || { hidden: [], role: null });
    } catch (err) { setError(err.message); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);
  // The running clocks move between loads.
  useEffect(() => { const id = setInterval(() => setTick(n => n + 1), 15000); return () => clearInterval(id); }, []);

  const saveView = (v) => {
    setView(v);
    fetch(`${API}/api/effort/today/view`, { method: 'PUT', credentials: 'include', headers: authHeaders(true), body: JSON.stringify(v) })
      .then(r => { if (!r.ok) setError('Your choice of people could not be saved.'); })
      .catch(() => setError('Your choice of people could not be saved.'));
  };

  if (!data) {
    return <Panel T={T}><div style={{ color: T.textSub, fontSize: 14, padding: 24, textAlign: 'center' }} role={error ? 'alert' : undefined}>{error || 'Loading today…'}</div></Panel>;
  }

  const byId = {};
  for (const t of tickets || []) byId[String(t.id)] = t;
  const ticketFor = (id) => byId[String(id)] || { id: String(id), displayId: `SVC-${id}`, customer: '', bike: '', status: '' };
  const now = Date.now() + skew;

  // ── Over budget ──
  const rows = [];
  let unknownLabor = 0;
  for (const w of data.tickets || []) {
    const t = byId[String(w.workorderId)];
    if (!t || !w.targetRate || w.seconds < MIN_SECONDS) continue;   // closed, no target, or too little time
    if (t.laborBilled === null || t.laborBilled === undefined) { unknownLabor++; continue; }
    const hours = w.seconds / 3600;
    const rate = t.laborBilled / hours;
    if (rate >= w.targetRate) continue;
    rows.push({ t, w, rate, shortfall: w.targetRate * hours - t.laborBilled });
  }
  rows.sort((a, b) => b.shortfall - a.shortfall);
  const th = { textAlign: 'left', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: c.sub, padding: '8px 10px', borderBottom: `1px solid ${T.cardBorder}` };
  const td = { padding: '10px', fontSize: 14, color: T.text, borderBottom: `1px solid ${T.cardBorder}`, verticalAlign: 'middle' };
  const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && <div role="alert" style={{ fontSize: 14, color: '#e74c3c' }}>{error}</div>}

      {data.needsReview > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12,
          background: isDark ? '#2a1d0d' : '#fdf1e3', border: '1px solid #e67e2266', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>
            {data.needsReview} timer{data.needsReview === 1 ? ' was' : 's were'} stopped automatically
          </span>
          <span style={{ fontSize: 13, color: c.sub }}>Check the times before they go on anyone's hours.</span>
          <button type="button" onClick={onReview}
            style={{ marginLeft: 'auto', border: 'none', borderRadius: 8, background: '#e67e22', color: '#fff', fontWeight: 700, fontSize: 13, padding: '8px 14px', fontFamily: 'inherit', cursor: 'pointer', minHeight: 36 }}>
            Review
          </button>
        </div>
      )}

      <Panel T={T} title="The shop right now"
        right={data.techs.length ? <WhoFilter T={T} c={c} techs={data.techs} view={view || { hidden: [], role: null }} onChange={saveView} /> : null}>
        {data.techs.length === 0 ? <Empty T={T}>No one is linked to a Lightspeed employee yet. Link logins in Settings, Users.</Empty>
          : visiblePeople(data.techs, view).length === 0 ? (
            <Empty T={T}>No one matches your choice. <button type="button" onClick={() => saveView({ hidden: [], role: null })}
              style={{ background: 'none', border: 'none', color: '#e67e22', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0 }}>Show everyone</button></Empty>
          )
          : visiblePeople(data.techs, view).map(p => {
            const on = p.running || p.last;
            const t = on ? ticketFor(on.workorderId) : null;
            const state = p.running
              ? <span style={{ fontSize: 13, fontWeight: 700, color: c.good }}>Timer running · {clock(now - new Date(p.running.startedAt))}</span>
              : p.last
                ? <span style={{ fontSize: 13, color: p.last.endReason === 'auto' ? c.warn : c.sub }}>
                    {p.last.endReason === 'auto' ? 'Stopped automatically' : p.last.endReason === 'stop' ? 'Stopped' : 'Paused'} at {fmtTime(p.last.endedAt)}
                  </span>
                : <span style={{ fontSize: 13, color: c.sub }}>No time today</span>;
            return (
              <Row key={p.userId} T={T} isDark={isDark} onOpen={t ? () => onOpenTicket(t) : undefined} title={t ? 'Open on the bench' : undefined}>
                <span style={{ fontWeight: 700, fontSize: 15, minWidth: 140, color: T.text }}>{p.name}</span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  {state}
                  {t && (
                    <span style={{ fontSize: 13, color: c.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.running ? '' : 'last on '}SVC-{t.id}{t.customer ? ` · ${t.customer}` : ''}{t.bike && t.bike !== '—' ? ` · ${t.bike}` : ''}
                    </span>
                  )}
                </span>
                <span style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <span style={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 18, fontVariantNumeric: 'tabular-nums', color: T.text }}>{fmtDur(p.secondsToday)}</span>
                  <span style={{ display: 'block', fontSize: 12, color: c.sub }}>today</span>
                </span>
              </Row>
            );
          })}
      </Panel>

      <Panel T={T} title="Over budget"
        right={data.targetRate ? 'open tickets earning less than the target rate for the time spent' : null}>
        {!data.targetRate ? (
          <Empty T={T}>Set a target labor rate in Settings, Workflow, Tech timer, and tickets earning less than it per hour worked show up here.</Empty>
        ) : rows.length === 0 ? (
          <Empty T={T}>Every open ticket with at least 15 minutes on the timer is at or above {fmtRate(data.targetRate)}.</Empty>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={th}>Ticket</th><th style={th}>Customer</th><th style={th}>Worked by</th>
                  <th style={{ ...th, textAlign: 'right' }}>Labor billed</th><th style={{ ...th, textAlign: 'right' }}>Time worked</th>
                  <th style={{ ...th, textAlign: 'right' }}>Rate</th><th style={{ ...th, textAlign: 'right' }}>Target</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ t, w, rate }) => (
                  <tr key={t.id} onClick={() => onOpenTicket(t)} style={{ cursor: 'pointer' }} title="Open on the bench">
                    <td style={td}><IdTag id={t.id} /></td>
                    <td style={td}><div style={{ fontWeight: 600 }}>{t.customer}</div><div style={{ fontSize: 12, color: c.sub }}>{t.status}</div></td>
                    <td style={{ ...td, color: c.sub }}>{w.techs.join(', ')}</td>
                    <td style={num}>{fmtMoney(t.laborBilled)}</td>
                    <td style={num}>{fmtDur(w.seconds)}</td>
                    <td style={{ ...num, fontWeight: 700, color: c.bad }}>{fmtRate(rate)}</td>
                    <td style={{ ...num, color: c.sub }}>{fmtRate(w.targetRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {unknownLabor > 0 && (
          <div style={{ fontSize: 12, color: c.sub }}>{unknownLabor} ticket{unknownLabor === 1 ? '' : 's'} left out: the labor charged could not be read from Lightspeed just now.</div>
        )}
        <div style={{ fontSize: 12, color: c.sub }}>
          Labor billed is the ticket's labor lines after discounts, not parts or misc. Time is every timer session on the ticket, by anyone.
        </div>
      </Panel>
    </div>
  );
}
